import { useAdminText } from './shared/useAdminText';
import { AdminTable } from './shared/AdminDataGrid';
import { PORTRAIT_ANSWER_THRESHOLD } from '@rekindle/shared/constants/portrait';
/**
 * AI Configuration Panel
 * Database-driven AI model/pricing/service configuration
 * 4 tabs: Tier Configs, Pricing, Provider Catalog, Backend Services
 */

import { useState, useEffect, useMemo } from 'react';
import { useNotify, Title } from 'react-admin';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Paper,
  Switch,
  MenuItem,
  FormControlLabel,
} from '@mui/material';
import type { SyntheticEvent } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import { apiClient } from '../providers/apiClient';
import { ContextValidationPanel } from './ContextValidationPanel';
import { BackendModelValidationPanel } from './BackendModelValidationPanel';
import { ChatEvaluationPanel } from './ChatEvaluationPanel';
import { logger } from '../utils/logger';

// ── Types ─────────────────────────────────────────────────────────────────────

interface TierAIConfig {
  id: string;
  tier: string;
  primaryModel: string;
  primaryModelProvider: string;
  fallbackModel: string | null;
  fallbackModelProvider: string | null;
  maxTokens: number;
  updatedAt: string;
}

interface ModelPricingEntry {
  id: string;
  provider: string;
  model: string;
  inputCostPer1M: number;
  outputCostPer1M: number;
  cachedInputCostPer1M: number | null;
  effectiveFrom: string;
  effectiveTo: string | null;
}

interface ProviderCatalogEntry {
  id: string;
  provider: string;
  model: string;
  displayName: string;
  supportsChat: boolean;
  supportsVision: boolean;
  supportsEmbedding: boolean;
  supportsReasoning: boolean;
  supportsSafeguard: boolean;
  supportsStructuredDecision: boolean;
  maxContextTokens: number | null;
  tierRecommendation: string | null;
}

interface BackendServiceConfig {
  id: string;
  serviceName: string;
  primaryModel: string;
  primaryModelProvider: string;
  fallbackModel: string | null;
  fallbackModelProvider: string | null;
  maxTokens: number;
  temperature: number;
  embeddingDimensions: number | null;
  isEnabled: boolean;
  decisionSettings: DecisionSettings | null;
  updatedAt: string;
}

type CatalogFilter = 'participation' | 'chat' | 'safety' | 'vision' | 'embedding' | 'structured_decision';
interface DecisionSettings { mode: 'observe' | 'apply'; threshold: number; timeoutMs: number; candidateLimit: number; repairLimit: number }
const DECISION_DEFAULTS: DecisionSettings = { mode: 'observe', threshold: 0.95, timeoutMs: 1500, candidateLimit: 8, repairLimit: 1 };
const DECISION_SERVICES = ['memory_relevance', 'suggestion_quality', 'chat_scope'];
const isDecisionService = (name: string) => DECISION_SERVICES.includes(name);

interface CatalogTestResult {
  success: boolean;
  capability: 'chat' | 'vision' | 'embedding' | 'safeguard' | 'structured_decision' | 'unknown';
  latencyMs: number;
  preview: string | null;
  tokensUsed: { input: number; output: number } | null;
  error: string | null;
}

// ── Shared model selector ─────────────────────────────────────────────────────

function ModelSelect({
  catalog,
  filter,
  label,
  value,
  onChange,
  allowNone,
}: {
  catalog: ProviderCatalogEntry[];
  filter: CatalogFilter;
  label: string;
  value: { provider: string; model: string };
  onChange: (val: { provider: string; model: string }) => void;
  allowNone?: boolean;
}) {
  const options = useMemo(() => {
    return catalog.filter((e) => {
      if (filter === 'participation') return e.supportsChat || (e.supportsStructuredDecision && e.provider === 'openrouter');
      if (filter === 'chat') return e.supportsChat;
      if (filter === 'safety') return e.supportsSafeguard || e.supportsChat;
      if (filter === 'vision') return e.supportsVision;
      if (filter === 'embedding') return e.supportsEmbedding;
      if (filter === 'structured_decision') return e.supportsStructuredDecision && e.provider === 'openrouter';
      return true;
    });
  }, [catalog, filter]);

  const selected = options.find(
    (o) => o.provider === value.provider && o.model === value.model
  ) ?? null;

  return (
    <Autocomplete
      size="small"
      options={options}
      value={selected}
      onChange={(_e, entry) => {
        if (entry) {
          onChange({ provider: entry.provider, model: entry.model });
        } else if (allowNone) {
          onChange({ provider: '', model: '' });
        }
      }}
      getOptionLabel={(o) => `${o.displayName} (${o.provider}/${o.model})`}
      groupBy={(o) => o.provider}
      isOptionEqualToValue={(o, v) => o.provider === v.provider && o.model === v.model}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={allowNone ? 'None' : undefined} />
      )}
      disableClearable={!allowNone}
      fullWidth
    />
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function AIConfigPanel() {
  const notify = useNotify();
  const [activeTab, setActiveTab] = useState(0);

  const [tiers, setTiers] = useState<TierAIConfig[]>([]);
  const [pricing, setPricing] = useState<ModelPricingEntry[]>([]);
  const [catalog, setCatalog] = useState<ProviderCatalogEntry[]>([]);
  const [services, setServices] = useState<BackendServiceConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [minimumAnsweredCards, setMinimumAnsweredCards] = useState('0');
  const [savingMinimum, setSavingMinimum] = useState(false);
  const [minimumLoaded, setMinimumLoaded] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [tiersData, pricingData, catalogData, servicesData, personalization] = await Promise.all([
          apiClient.get<TierAIConfig[]>('/admin/ai-config/tiers'),
          apiClient.get<ModelPricingEntry[]>('/admin/ai-config/pricing'),
          apiClient.get<ProviderCatalogEntry[]>('/admin/ai-config/catalog'),
          apiClient.get<BackendServiceConfig[]>('/admin/ai-config/services'),
          apiClient.get<{ minimumAnsweredCards: number }>('/admin/ai-config/personalization'),
        ]);
        setTiers(tiersData);
        setPricing(pricingData);
        setCatalog(catalogData);
        setServices(servicesData);
        setMinimumAnsweredCards(String(personalization.minimumAnsweredCards));
        setMinimumLoaded(true);
      } catch (err) {
        logger.error('Failed to load AI config', err);
        notify('Failed to load AI configuration', { type: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [notify]);

  const handleTierUpdate = (updated: TierAIConfig) => {
    setTiers((prev) => prev.map((t) => (t.tier === updated.tier ? updated : t)));
  };

  const handleServiceUpdate = (updated: BackendServiceConfig) => {
    setServices((prev) => prev.map((s) => (s.serviceName === updated.serviceName ? updated : s)));
  };

  const handlePricingAdd = (added: ModelPricingEntry) => {
    setPricing((prev) => [...prev, added]);
  };

  const handlePricingEnd = (id: string) => {
    setPricing((prev) => prev.filter((p) => p.id !== id));
  };

  return (
    <Box>
      <Title title="AI Configuration" />
      <Tabs value={activeTab} onChange={(_e: SyntheticEvent, v: number) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 3 }}>
        <Tab label="Tier Configs" />
        <Tab label="Model Pricing" />
        <Tab label="Provider Catalog" />
        <Tab label="Backend Services" />
      </Tabs>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6">Personal context</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              One requirement for private chat insights and partner suggestions. Only Yes/No answers count.
              Zero allows questionnaire-only users. Written portraits still require {PORTRAIT_ANSWER_THRESHOLD} answered cards.
            </Typography>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <TextField label="Minimum answered cards" type="number" size="small" sx={{ width: 240, maxWidth: '100%' }}
                value={minimumAnsweredCards} onChange={e => setMinimumAnsweredCards(e.target.value)}
                slotProps={{ htmlInput: { min: 0, max: 2147483647, step: 1 } }} />
              <Button variant="contained" disabled={!minimumLoaded || savingMinimum || !/^\d+$/.test(minimumAnsweredCards) || Number(minimumAnsweredCards) > 2147483647}
                onClick={async () => {
                  setSavingMinimum(true);
                  try {
                    const result = await apiClient.put<{ minimumAnsweredCards: number }>('/admin/ai-config/personalization', { minimumAnsweredCards: Number(minimumAnsweredCards) });
                    setMinimumAnsweredCards(String(result.minimumAnsweredCards));
                    notify('Minimum answered cards saved', { type: 'success' });
                  } catch (error) {
                    logger.error('Failed to save personalization setting', error);
                    notify('Could not save the minimum answered cards', { type: 'error' });
                  } finally { setSavingMinimum(false); }
                }}>Save requirement</Button>
            </Box>
            <ContextValidationPanel />
          </Paper>
          {activeTab === 0 && <TiersTab tiers={tiers} catalog={catalog} onUpdate={handleTierUpdate} />}
          {activeTab === 1 && <PricingTab pricing={pricing} catalog={catalog} onAdd={handlePricingAdd} onEnd={handlePricingEnd} />}
          {activeTab === 2 && <CatalogTab catalog={catalog} />}
          {activeTab === 3 && <ServicesTab services={services} catalog={catalog} onUpdate={handleServiceUpdate} />}
        </>
      )}
    </Box>
  );
}

// ── Tier configs tab ──────────────────────────────────────────────────────────

function TiersTab({ tiers, catalog, onUpdate }: { tiers: TierAIConfig[]; catalog: ProviderCatalogEntry[]; onUpdate: (t: TierAIConfig) => void }) {
  const [editTier, setEditTier] = useState<TierAIConfig | null>(null);

  if (tiers.length === 0) {
    return <Typography color="text.secondary">No tier configs found. Run database seed.</Typography>;
  }
  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <AdminTable size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Tier</strong></TableCell>
              <TableCell><strong>Primary Model</strong></TableCell>
              <TableCell><strong>Fallback</strong></TableCell>
              <TableCell><strong>Max Tokens</strong></TableCell>
              <TableCell><strong>Updated</strong></TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {tiers.map((cfg) => (
              <TableRow key={cfg.tier} hover>
                <TableCell>
                  <Chip label={cfg.tier} size="small" color="primary" />
                </TableCell>
                <TableCell>{cfg.primaryModelProvider}/{cfg.primaryModel}</TableCell>
                <TableCell>{cfg.fallbackModel ? `${cfg.fallbackModelProvider}/${cfg.fallbackModel}` : '\u2014'}</TableCell>
                <TableCell>{cfg.maxTokens.toLocaleString()}</TableCell>
                <TableCell>{new Date(cfg.updatedAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => setEditTier(cfg)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </AdminTable>
      </TableContainer>

      {editTier && (
        <EditTierDialog
          tier={editTier}
          catalog={catalog}
          onClose={() => setEditTier(null)}
          onSaved={(updated) => { onUpdate(updated); setEditTier(null); }}
        />
      )}
    </>
  );
}

// ── Edit tier dialog ─────────────────────────────────────────────────────────

function EditTierDialog({
  tier,
  catalog,
  onClose,
  onSaved,
}: {
  tier: TierAIConfig;
  catalog: ProviderCatalogEntry[];
  onClose: () => void;
  onSaved: (t: TierAIConfig) => void;
}) {
  const notify = useNotify();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    primaryModel: tier.primaryModel,
    primaryModelProvider: tier.primaryModelProvider,
    fallbackModel: tier.fallbackModel ?? '',
    fallbackModelProvider: tier.fallbackModelProvider ?? '',
    maxTokens: tier.maxTokens,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        primaryModel: form.primaryModel,
        primaryModelProvider: form.primaryModelProvider,
        maxTokens: form.maxTokens,
      };
      body.fallbackModel = form.fallbackModel || null;
      body.fallbackModelProvider = form.fallbackModelProvider || null;

      const updated = await apiClient.put<TierAIConfig>(`/admin/ai-config/tiers/${tier.tier}`, body);
      notify('Tier config updated', { type: 'success' });
      onSaved(updated);
    } catch (err) {
      logger.error('Failed to update tier config', err);
      notify('Failed to update tier config', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Tier: {tier.tier}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <Typography variant="subtitle2" color="text.secondary">Primary Model</Typography>
          <ModelSelect
            catalog={catalog}
            filter="chat"
            label="Primary Model"
            value={{ provider: form.primaryModelProvider, model: form.primaryModel }}
            onChange={({ provider, model }) => setForm((prev) => ({ ...prev, primaryModelProvider: provider, primaryModel: model }))}
          />

          <Typography variant="subtitle2" color="text.secondary">Fallback Model</Typography>
          <ModelSelect
            catalog={catalog}
            filter="chat"
            label="Fallback Model"
            value={{ provider: form.fallbackModelProvider, model: form.fallbackModel }}
            onChange={({ provider, model }) => setForm((prev) => ({ ...prev, fallbackModelProvider: provider, fallbackModel: model }))}
            allowNone
          />

          <TextField
            label="Max Tokens"
            size="small"
            type="number"
            value={form.maxTokens}
            onChange={(e) => setForm((prev) => ({ ...prev, maxTokens: Number(e.target.value) }))}
            inputProps={{ min: 256, max: 32768 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Pricing tab ───────────────────────────────────────────────────────────────

function PricingTab({
  pricing,
  catalog,
  onAdd,
  onEnd,
}: {
  pricing: ModelPricingEntry[];
  catalog: ProviderCatalogEntry[];
  onAdd: (entry: ModelPricingEntry) => void;
  onEnd: (id: string) => void;
}) {
  const notify = useNotify();
  const t = useAdminText();
  const [showAdd, setShowAdd] = useState(false);
  const [endingId, setEndingId] = useState<string | null>(null);

  const handleEnd = async (id: string) => {
    setEndingId(id);
    try {
      await apiClient.post(`/admin/ai-config/pricing/${id}/end`);
      notify('Pricing entry ended', { type: 'success' });
      onEnd(id);
    } catch (err) {
      logger.error('Failed to end pricing', err);
      notify('Failed to end pricing entry', { type: 'error' });
    } finally {
      setEndingId(null);
    }
  };

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Button variant="outlined" size="small" onClick={() => setShowAdd(true)}>
          Add Pricing
        </Button>
      </Box>

      {pricing.length === 0 ? (
        <Typography color="text.secondary">No active pricing found. Run database seed or add entries above.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <AdminTable size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Provider</strong></TableCell>
                <TableCell><strong>Model</strong></TableCell>
                <TableCell><strong>Input ($/1M)</strong></TableCell>
                <TableCell><strong>Output ($/1M)</strong></TableCell>
                <TableCell><strong>{t('cachedInputPrice', 'Cached input ($/1M)')}</strong></TableCell>
                <TableCell><strong>Effective From</strong></TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {pricing.map((entry) => (
                <TableRow key={entry.id} hover>
                  <TableCell>{entry.provider}</TableCell>
                  <TableCell>{entry.model}</TableCell>
                  <TableCell>${entry.inputCostPer1M}</TableCell>
                  <TableCell>${entry.outputCostPer1M}</TableCell>
                  <TableCell>{entry.cachedInputCostPer1M == null ? t('notConfigured', 'Not configured') : `$${entry.cachedInputCostPer1M}`}</TableCell>
                  <TableCell>{new Date(entry.effectiveFrom).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      color="warning"
                      disabled={endingId === entry.id}
                      onClick={() => handleEnd(entry.id)}
                    >
                      {endingId === entry.id ? 'Ending...' : 'End'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </AdminTable>
        </TableContainer>
      )}

      {showAdd && (
        <AddPricingDialog
          catalog={catalog}
          onClose={() => setShowAdd(false)}
          onSaved={(added) => { onAdd(added); setShowAdd(false); }}
        />
      )}
    </>
  );
}

// ── Add pricing dialog ──────────────────────────────────────────────────────

function AddPricingDialog({
  catalog,
  onClose,
  onSaved,
}: {
  catalog: ProviderCatalogEntry[];
  onClose: () => void;
  onSaved: (entry: ModelPricingEntry) => void;
}) {
  const notify = useNotify();
  const t = useAdminText();
  const [saving, setSaving] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupSource, setLookupSource] = useState<string | null>(null);
  const [form, setForm] = useState({
    provider: '',
    model: '',
    inputCostPer1M: 0,
    outputCostPer1M: 0,
    cachedInputCostPer1M: '',
  });

  const selectedCatalogEntry = catalog.find(
    (e) => e.provider === form.provider && e.model === form.model
  ) ?? null;

  const handleLookup = async () => {
    setLookingUp(true);
    setLookupSource(null);
    try {
      const pricing = await apiClient.post<{
        catalogKey: string; inputCostPer1M: number; outputCostPer1M: number;
      }>('/admin/ai-config/pricing/lookup', { provider: form.provider, model: form.model });
      setForm((prev) => ({ ...prev, inputCostPer1M: pricing.inputCostPer1M, outputCostPer1M: pricing.outputCostPer1M, cachedInputCostPer1M: '' }));
      setLookupSource(pricing.catalogKey);
    } catch (err) {
      logger.error('Failed to look up model pricing', err);
      notify('LiteLLM pricing unavailable for this model. Enter prices manually.', { type: 'warning' });
    } finally {
      setLookingUp(false);
    }
  };

  const handleSave = async () => {
    if (!form.provider || !form.model) {
      notify('Provider and model are required', { type: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const added = await apiClient.post<ModelPricingEntry>('/admin/ai-config/pricing', { ...form,
        cachedInputCostPer1M: form.cachedInputCostPer1M === '' ? null : Number(form.cachedInputCostPer1M) });
      notify('Pricing entry added', { type: 'success' });
      onSaved(added);
    } catch (err) {
      logger.error('Failed to add pricing', err);
      notify('Failed to add pricing entry', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Model Pricing</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <Autocomplete
            size="small"
            options={catalog}
            disabled={lookingUp || saving}
            value={selectedCatalogEntry}
            onChange={(_e, entry) => {
              setLookupSource(null);
              if (entry) {
                setForm({ provider: entry.provider, model: entry.model, inputCostPer1M: 0, outputCostPer1M: 0, cachedInputCostPer1M: '' });
              } else {
                setForm({ provider: '', model: '', inputCostPer1M: 0, outputCostPer1M: 0, cachedInputCostPer1M: '' });
              }
            }}
            getOptionLabel={(o) => `${o.displayName} (${o.provider}/${o.model})`}
            groupBy={(o) => o.provider}
            isOptionEqualToValue={(o, v) => o.provider === v.provider && o.model === v.model}
            renderInput={(params) => <TextField {...params} label="Provider / Model" />}
            fullWidth
          />
          <Button onClick={handleLookup} disabled={lookingUp || saving || !form.model || !['openai', 'groq', 'openrouter'].includes(form.provider)}>
            {lookingUp ? 'Looking up...' : 'Look up with LiteLLM'}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {lookupSource ? `Loaded from LiteLLM (${lookupSource}). Review the prices, then click Add to save.` : 'Lookup only fills this form. Prices are saved when you click Add.'}
          </Typography>
          <TextField
            label={t('cachedInputPrice', 'Cached input ($/1M)')}
            helperText={t('cachedPriceHelp', 'Optional. Without a cache rate, estimates use the ordinary input price.')}
            size="small" type="number" disabled={lookingUp || saving}
            value={form.cachedInputCostPer1M}
            onChange={event => setForm(prev => ({ ...prev, cachedInputCostPer1M: event.target.value }))}
            inputProps={{ min: 0, step: 0.000001 }}
          />
          <Box display="flex" gap={2}>
            <TextField
              label="Input Cost ($/1M tokens)"
              disabled={lookingUp || saving}
              size="small"
              type="number"
              fullWidth
              value={form.inputCostPer1M}
              onChange={(e) => setForm((prev) => ({ ...prev, inputCostPer1M: Number(e.target.value) }))}
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              label="Output Cost ($/1M tokens)"
              disabled={lookingUp || saving}
              size="small"
              type="number"
              fullWidth
              value={form.outputCostPer1M}
              onChange={(e) => setForm((prev) => ({ ...prev, outputCostPer1M: Number(e.target.value) }))}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || lookingUp}>
          {saving ? 'Adding...' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Catalog test button ───────────────────────────────────────────────────────

type TestCapability = 'chat' | 'vision' | 'embedding' | 'safeguard' | 'structured_decision';

const CAPABILITY_LABELS: Record<TestCapability, string> = {
  structured_decision: 'Structured Decision',
  chat: 'Chat',
  vision: 'Vision',
  embedding: 'Embed',
  safeguard: 'Safeguard',
};

function CatalogTestButton({ catalogId, entry }: { catalogId: string; entry: ProviderCatalogEntry }) {
  const [testing, setTesting] = useState<Record<string, boolean>>({});
  const [results, setResults] = useState<Record<string, CatalogTestResult>>({});

  const capabilities = (
    [
      entry.supportsChat ? 'chat' : null,
      entry.supportsVision ? 'vision' : null,
      entry.supportsEmbedding ? 'embedding' : null,
      entry.supportsSafeguard ? 'safeguard' : null,
      entry.supportsStructuredDecision ? 'structured_decision' : null,
    ] as (TestCapability | null)[]
  ).filter((c): c is TestCapability => c !== null);

  const setResult = (capability: TestCapability, result: CatalogTestResult) => {
    setResults((prev) => ({ ...prev, [capability]: result }));
  };

  const handleTest = async (capability: TestCapability) => {
    setTesting((prev) => ({ ...prev, [capability]: true }));
    setResults((prev) => {
      const next = { ...prev };
      delete next[capability];
      return next;
    });
    try {
      const data = await apiClient.post<CatalogTestResult>(
        `/admin/ai-config/catalog/${catalogId}/test`,
        { capability }
      );
      setResult(capability, data);
    } catch (err) {
      logger.error('Catalog model test failed', err);
      setResult(capability, {
        success: false,
        capability: 'unknown',
        latencyMs: 0,
        preview: null,
        tokensUsed: null,
        error: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setTesting((prev) => ({ ...prev, [capability]: false }));
    }
  };

  if (capabilities.length === 0) return null;

  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      <Box display="flex" gap={0.5} flexWrap="wrap">
        {capabilities.map((cap) => (
          <Button
            key={cap}
            size="small"
            variant="outlined"
            disabled={!!testing[cap]}
            onClick={() => handleTest(cap)}
            data-testid={`catalog-test-button-${catalogId}-${cap}`}
            startIcon={testing[cap] ? <CircularProgress size={10} /> : undefined}
          >
            {CAPABILITY_LABELS[cap]}
          </Button>
        ))}
      </Box>
      {entry.supportsStructuredDecision && <DecisionValidationButton catalogId={catalogId} />}
      {capabilities.filter((cap) => results[cap]).map((cap) => {
        const result = results[cap];
        return (
          <Typography
            key={cap}
            variant="caption"
            role="status"
            aria-live="polite"
            sx={{ color: result.success ? 'success.main' : 'error.main', maxWidth: 300 }}
          >
            {result.success ? '✅' : '❌'} {CAPABILITY_LABELS[cap]}: {result.latencyMs}ms
            {result.preview ? ` · "${result.preview.slice(0, 50)}"` : ''}
            {result.error ? ` · ${result.error}` : ''}
          </Typography>
        );
      })}
    </Box>
  );
}

interface DecisionValidationReport {
  suite: 'accuracy' | 'repair' | 'generation'; fixtureVersion: string; promptVersion: string; model: string; threshold: number; timeoutMs: number;
  judgments: Array<{ caseId: string; feature: string; item: string; expectedReject: boolean | null;
    actualReject: boolean | null; probability: number | null; passed: boolean | null; criteria?: Record<string, number> }>;
  examples: Array<{ caseId: string; evidence: string; before: string[]; after: string[]; error: string | null }>;
  decisionCalls: number; generationCalls: number; inputTokens: number; outputTokens: number;
  decisionCostUSD: number; generationCostUSD: number | null; elapsedMs: number;
}

function DecisionValidationButton({ catalogId }: { catalogId: string }) {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState<DecisionValidationReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (suite: DecisionValidationReport['suite']) => {
    setRunning(true); setError(null); setReport(null);
    try {
      const result = await apiClient.post<{ validation: DecisionValidationReport }>(
        `/admin/ai-config/catalog/${catalogId}/test`, { capability: 'structured_decision', validationSuite: suite },
        { timeout: 60000, retries: 1 });
      if (!result.validation) throw new Error('Validation report unavailable');
      setReport(result.validation);
    } catch (err) { setError(err instanceof Error ? err.message : 'Validation failed'); }
    finally { setRunning(false); }
  };
  const labeled = report?.judgments.filter(row => row.expectedReject !== null) ?? [];
  return <>
    <Button size="small" onClick={() => setOpen(true)}>Validate pilots</Button>
    <Dialog open={open} onClose={() => !running && setOpen(false)} maxWidth="lg" fullWidth>
      <DialogTitle>Structured Decision validation</DialogTitle>
      <DialogContent>
        <Typography mb={2}>Synthetic examples only. Uses the same pilot logic and live providers. Does not change user preferences, consume hearts or generation allowances, or add events to the pilot metrics. Provider calls have a cost.</Typography>
        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
          <Button variant="outlined" disabled={running} onClick={() => run('accuracy')}>Test decision accuracy</Button>
          <Button variant="outlined" disabled={running} onClick={() => run('repair')}>Test seeded repair</Button>
          <Button variant="outlined" disabled={running} onClick={() => run('generation')}>Compare generated batches</Button>
        </Box>
        <Typography variant="body2" color="text.secondary">Accuracy uses labels fixed before inference. Seeded repair starts with known defects. Generated batches use the configured Ember chat model and require manual review; the decision model is not an independent quality judge.</Typography>
        {running && <Box mt={2} role="status"><CircularProgress size={20} /> Running live validation…</Box>}
        {error && <Typography color="error" role="alert">{error}</Typography>}
        {report && <Box mt={2}>
          <Typography variant="h6">{report.suite} — {labeled.length
            ? `${labeled.filter(row => row.passed).length}/${labeled.length} labeled checks passed`
            : `${report.judgments.length} judgments; manual review required`}</Typography>
          <Typography variant="body2">Model {report.model}; fixture {report.fixtureVersion}; prompt {report.promptVersion}; threshold {report.threshold}; timeout {report.timeoutMs} ms. {report.decisionCalls} decision calls, {report.generationCalls} generation calls, {report.elapsedMs} ms total.</Typography>
          <Typography variant="body2">Decision cost: ${report.decisionCostUSD.toFixed(6)}. Generation cost: {report.generationCostUSD === null ? 'unavailable' : `$${report.generationCostUSD.toFixed(6)}`}.</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ my: 2 }}><AdminTable size="small">
            <TableHead><TableRow>{['Case', 'Item', 'Expected', 'Actual', 'Problem probability', 'Check'].map(label => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
            <TableBody>{report.judgments.map((row, index) => <TableRow key={`${row.caseId}-${index}`}>
              <TableCell>{row.caseId}</TableCell><TableCell>{row.item}</TableCell>
              <TableCell>{row.expectedReject === null ? 'Manual review' : row.expectedReject ? 'Reject' : 'Keep'}</TableCell>
              <TableCell>{row.actualReject === null ? 'Unavailable' : row.actualReject ? 'Reject' : 'Keep'}</TableCell>
              <TableCell>{row.probability?.toFixed(3) ?? '—'}
                {row.criteria && <Typography variant="caption" component="div">
                  {Object.entries(row.criteria).map(([name, value]) => `${name}: ${value.toFixed(3)}`).join(' · ')}
                </Typography>}
              </TableCell>
              <TableCell>{row.passed === null ? 'Review' : row.passed ? 'Pass' : 'Fail'}</TableCell>
            </TableRow>)}</TableBody>
          </AdminTable></TableContainer>
          {report.examples.map(example => <Box key={example.caseId} mb={3}>
            <Typography variant="subtitle1">{example.caseId}</Typography>
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>Evidence: {example.evidence}</Typography>
            <Typography variant="subtitle2" mt={1}>Before</Typography>{example.before.map((text, i) => <Typography key={i} variant="body2" paragraph>{text}</Typography>)}
            <Typography variant="subtitle2">After</Typography>{example.after.map((text, i) => <Typography key={i} variant="body2" paragraph>{text}</Typography>)}
            {example.error && <Typography color="error">{example.error}</Typography>}
          </Box>)}
        </Box>}
      </DialogContent>
      <DialogActions><Button disabled={running} onClick={() => setOpen(false)}>Close</Button></DialogActions>
    </Dialog>
  </>;
}

// ── Provider catalog tab ──────────────────────────────────────────────────────

function CatalogTab({ catalog }: { catalog: ProviderCatalogEntry[] }) {
  if (catalog.length === 0) {
    return <Typography color="text.secondary">No models in catalog. Run database seed.</Typography>;
  }
  return (
    <TableContainer component={Paper} variant="outlined">
      <AdminTable size="small">
        <TableHead>
          <TableRow>
            <TableCell><strong>Display Name</strong></TableCell>
            <TableCell><strong>Provider/Model</strong></TableCell>
            <TableCell><strong>Capabilities</strong></TableCell>
            <TableCell><strong>Context</strong></TableCell>
            <TableCell><strong>Recommended For</strong></TableCell>
            <TableCell><strong>Test</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {catalog.map((entry) => (
            <TableRow key={entry.id} hover>
              <TableCell>{entry.displayName}</TableCell>
              <TableCell>{entry.provider}/{entry.model}</TableCell>
              <TableCell>
                <Box display="flex" gap={0.5} flexWrap="wrap">
                  {entry.supportsStructuredDecision && <Chip label="Structured Decision" size="small" color="info" />}
                  {entry.supportsChat && <Chip label="Chat" size="small" />}
                  {entry.supportsVision && <Chip label="Vision" size="small" color="info" />}
                  {entry.supportsEmbedding && <Chip label="Embed" size="small" color="secondary" />}
                  {entry.supportsReasoning && <Chip label="Reason" size="small" color="success" />}
                  {entry.supportsSafeguard && <Chip label="Safeguard" size="small" color="warning" />}
                </Box>
              </TableCell>
              <TableCell>{entry.maxContextTokens ? `${(entry.maxContextTokens / 1000).toFixed(0)}K` : '\u2014'}</TableCell>
              <TableCell>{entry.tierRecommendation ?? '\u2014'}</TableCell>
              <TableCell>
                <CatalogTestButton catalogId={entry.id} entry={entry} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </AdminTable>
    </TableContainer>
  );
}

// ── Backend services tab ──────────────────────────────────────────────────────

const INFRASTRUCTURE_SERVICES = ['vision', 'embedding'];

function getServiceModelFilter(serviceName: string): CatalogFilter {
  if (serviceName === 'content_moderation') return 'safety';
  if (serviceName === 'chat_participation') return 'participation';
  if (serviceName === 'vision') return 'vision';
  if (serviceName === 'embedding') return 'embedding';
  if (isDecisionService(serviceName)) return 'structured_decision';
  return 'chat';
}

function ServicesTab({ services, catalog, onUpdate }: { services: BackendServiceConfig[]; catalog: ProviderCatalogEntry[]; onUpdate: (s: BackendServiceConfig) => void }) {
  const [editService, setEditService] = useState<BackendServiceConfig | null>(null);

  if (services.length === 0) {
    return <Typography color="text.secondary">No backend service configs. Run database seed.</Typography>;
  }

  const infrastructureServices = services.filter(s => INFRASTRUCTURE_SERVICES.includes(s.serviceName));
  const textServices = services.filter(s => !INFRASTRUCTURE_SERVICES.includes(s.serviceName) && !isDecisionService(s.serviceName) && !['crisis_resources', 'content_moderation'].includes(s.serviceName));
  const decisionServices = services.filter(s => isDecisionService(s.serviceName) && s.serviceName !== 'chat_scope');
  const scopeServices = ['content_moderation', 'chat_scope'].flatMap(name => services.filter(s => s.serviceName === name));

  const renderServiceRow = (svc: BackendServiceConfig) => (
    <TableRow key={svc.serviceName} hover>
      <TableCell>{svc.serviceName.replace(/_/g, ' ')}</TableCell>
      <TableCell>{svc.primaryModelProvider}/{svc.primaryModel}</TableCell>
      <TableCell>{svc.fallbackModel ? `${svc.fallbackModelProvider}/${svc.fallbackModel}` : '\u2014'}</TableCell>
      <TableCell>{isDecisionService(svc.serviceName) ? '—' : svc.maxTokens.toLocaleString()}</TableCell>
      <TableCell>{isDecisionService(svc.serviceName) ? '—' : svc.temperature}</TableCell>
      <TableCell>
        {svc.serviceName === 'embedding' ? `${svc.embeddingDimensions}d` : '\u2014'}
      </TableCell>
      <TableCell>
        <Chip
          label={svc.isEnabled ? (isDecisionService(svc.serviceName) ? (svc.decisionSettings?.mode === 'apply' ? 'Apply' : 'Observe') : 'Enabled') : 'Disabled'}
          size="small"
          color={svc.isEnabled ? 'success' : 'default'}
        />
      </TableCell>
      <TableCell>
        {svc.serviceName === 'embedding' ? (
          <Tooltip title="Embedding model changes require a full vector re-index. Contact engineering.">
            <span>
              <IconButton size="small" disabled>
                <LockIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <IconButton size="small" aria-label={`Edit ${svc.serviceName.replace(/_/g, ' ')}`} onClick={() => svc.serviceName !== 'embedding' && setEditService(svc)}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  );

  const renderSectionHeader = (groupName: string) => (
    <TableRow>
      <TableCell colSpan={8} sx={{ bgcolor: 'grey.100', py: 0.5, borderBottom: 'none' }}>
        <Typography variant="caption" fontWeight={600} color="text.secondary">
          {groupName}
        </Typography>
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <Box mb={3}>
        <Typography variant="h6" mb={0.5}>Chatbot scope</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Safety is checked first for self-harm and harmful requests. Requests that pass safety are checked for scope before Elisa answers.
          Both safety responses and scope refusals follow the current message’s language. Scope outages use Elisa’s scope instructions; safety outages defer the answer.
        </Typography>
        <TableContainer component={Paper} variant="outlined"><AdminTable size="small" aria-label="Chatbot scope configuration">
          <TableHead><TableRow><TableCell>Check</TableCell><TableCell>Model</TableCell><TableCell>Status</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>{scopeServices.map(svc => <TableRow key={svc.serviceName}>
            <TableCell>{svc.serviceName === 'content_moderation' ? '1. Safety (input and output)' : '2. Scope'}</TableCell>
            <TableCell>{catalog.find(e => e.provider === svc.primaryModelProvider && e.model === svc.primaryModel)?.displayName ?? svc.primaryModel}</TableCell>
            <TableCell>{!svc.isEnabled ? 'Disabled' : svc.serviceName === 'chat_scope' ? svc.decisionSettings?.mode === 'apply' ? 'Enforced' : 'Observation' : 'Enabled'}</TableCell>
            <TableCell><Button size="small" onClick={() => setEditService(svc)} aria-label={`Configure ${svc.serviceName.replace(/_/g, ' ')}`}>Configure</Button></TableCell>
          </TableRow>)}</TableBody>
        </AdminTable></TableContainer>
        {!scopeServices.some(s => s.serviceName === 'chat_scope') && <Typography variant="body2" color="text.secondary">Apply the chat scope database migration to configure the scope check.</Typography>}
      </Box>
      <BackendModelValidationPanel />
      <ChatEvaluationPanel />
      <Typography variant="body2" color="text.secondary" mb={2}>Crisis contacts come from the reviewed country directory and do not use an AI model.</Typography>
      {decisionServices.length > 0 && <Box mb={3}>
        <Typography variant="h6" mb={0.5}>Structured Decision</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>Choose an intermediate model for each feature. Enable independently, observe evaluations, or apply improvements.</Typography>
        <TableContainer component={Paper} variant="outlined"><AdminTable size="small">
          <TableHead><TableRow>
            <TableCell>Feature</TableCell><TableCell>Primary Model</TableCell><TableCell>Fallback</TableCell><TableCell>Status</TableCell><TableCell />
          </TableRow></TableHead>
          <TableBody>{decisionServices.map(svc => <TableRow key={svc.serviceName} hover>
            <TableCell>{svc.serviceName === 'memory_relevance' ? 'Memory relevance' : 'Suggestion quality'}</TableCell>
            <TableCell>{catalog.find(e => e.provider === svc.primaryModelProvider && e.model === svc.primaryModel)?.displayName ?? svc.primaryModel}</TableCell>
            <TableCell>{svc.fallbackModel ?? 'None'}</TableCell>
            <TableCell><Chip size="small" label={!svc.isEnabled ? 'Disabled' : svc.decisionSettings?.mode === 'apply' ? 'Apply' : 'Observe'} color={svc.isEnabled ? 'success' : 'default'} /></TableCell>
            <TableCell><Button size="small" onClick={() => setEditService(svc)} aria-label={`Configure ${svc.serviceName.replace(/_/g, ' ')}`}>Configure</Button></TableCell>
          </TableRow>)}</TableBody>
        </AdminTable></TableContainer>
      </Box>}
      {(infrastructureServices.length > 0 || textServices.length > 0) && <TableContainer component={Paper} variant="outlined">
        <AdminTable size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Service</strong></TableCell>
              <TableCell><strong>Primary Model</strong></TableCell>
              <TableCell><strong>Fallback</strong></TableCell>
              <TableCell><strong>Max Tokens</strong></TableCell>
              <TableCell><strong>Temperature</strong></TableCell>
              <TableCell><strong>Dimensions</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {infrastructureServices.length > 0 && (
              <>
                {renderSectionHeader('Infrastructure Models')}
                {infrastructureServices.map(renderServiceRow)}
              </>
            )}
            {textServices.length > 0 && (
              <>
                {renderSectionHeader('Text Generation')}
                {textServices.map(renderServiceRow)}
              </>
            )}
          </TableBody>
        </AdminTable>
      </TableContainer>}

      {decisionServices.length > 0 && <DecisionMetrics />}
      {editService && (
        <EditServiceDialog
          service={editService}
          catalog={catalog}
          onClose={() => setEditService(null)}
          onSaved={(updated) => { onUpdate(updated); setEditService(null); }}
        />
      )}
    </>
  );
}

// ── Edit service dialog ──────────────────────────────────────────────────────

interface DecisionMetric {
  serviceName: string; mode: string; status: string; calls: number; costUSD: number;
  averageLatencyMs: number; candidates: number; flagged: number; applied: number; repaired: number;
}

function DecisionMetrics() {
  const [rows, setRows] = useState<DecisionMetric[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const refresh = async () => {
    setLoading(true); setError(false);
    try { setRows(await apiClient.get<DecisionMetric[]>('/admin/ai-config/decisions/stats')); }
    catch { setError(true); }
    finally { setLoading(false); }
  };
  useEffect(() => { void refresh(); }, []);
  return <Box mt={3}>
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography variant="subtitle1">Structured Decision — last 7 days</Typography>
      <Button onClick={refresh} disabled={loading}>Refresh</Button>
    </Box>
    <Typography variant="body2" color="text.secondary">Evaluation costs are additional provider costs, not extra hearts. Flagged means a potential issue; applied means a memory was filtered or a suggestion replaced. Metrics contain no private text.</Typography>
    {error ? <Typography role="alert" color="error">Metrics could not be loaded. Try Refresh.</Typography> :
      loading ? <CircularProgress size={20} /> :
      rows.length === 0 ? <Typography color="text.secondary">No evaluations recorded yet. Enable Observe mode to begin.</Typography> :
      <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}><AdminTable size="small">
        <TableHead><TableRow>{['Feature', 'Mode', 'Outcome', 'Evaluations / events', 'Cost (USD)', 'Mean latency', 'Candidates', 'Flagged', 'Applied', 'Repaired'].map(label =>
          <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
        <TableBody>{rows.map(row => <TableRow key={`${row.serviceName}-${row.mode}-${row.status}`}>
          <TableCell>{row.serviceName.replace(/_/g, ' ')}</TableCell><TableCell>{row.mode}</TableCell>
          <TableCell>{row.status.replace(/_/g, ' ')}</TableCell><TableCell>{row.calls}</TableCell>
          <TableCell>${row.costUSD.toFixed(6)}</TableCell><TableCell>{Math.round(row.averageLatencyMs)} ms</TableCell>
          <TableCell>{row.candidates}</TableCell><TableCell>{row.flagged}</TableCell>
          <TableCell>{row.applied}</TableCell><TableCell>{row.repaired}</TableCell>
        </TableRow>)}</TableBody>
      </AdminTable></TableContainer>}
    <Typography variant="caption" color="text.secondary">Memory relevance runs on historical search results in production. The catalog's Structured Decision test uses synthetic memories and also works in preview.</Typography>
  </Box>;
}

function EditServiceDialog({
  service,
  catalog,
  onClose,
  onSaved,
}: {
  service: BackendServiceConfig;
  catalog: ProviderCatalogEntry[];
  onClose: () => void;
  onSaved: (s: BackendServiceConfig) => void;
}) {
  const notify = useNotify();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    primaryModel: service.primaryModel,
    primaryModelProvider: service.primaryModelProvider,
    fallbackModel: service.fallbackModel ?? '',
    fallbackModelProvider: service.fallbackModelProvider ?? '',
    maxTokens: service.maxTokens,
    temperature: service.temperature,
    isEnabled: service.isEnabled,
    decisionSettings: service.decisionSettings ?? { ...DECISION_DEFAULTS, ...(service.serviceName === 'chat_participation' ? { threshold: 0.65 } : {}), ...(service.serviceName === 'chat_scope' ? { timeoutMs: 3000, candidateLimit: 1, repairLimit: 0 } : {}) },
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        primaryModel: form.primaryModel,
        primaryModelProvider: form.primaryModelProvider,
        maxTokens: form.maxTokens,
        temperature: form.temperature,
        isEnabled: form.isEnabled,
      };
      if (isDecisionService(service.serviceName) || service.serviceName === 'chat_participation') {
        body.decisionSettings = form.decisionSettings;
        delete body.maxTokens;
        delete body.temperature;
      }
      body.fallbackModel = form.fallbackModel || null;
      body.fallbackModelProvider = form.fallbackModelProvider || null;

      const updated = await apiClient.put<BackendServiceConfig>(
        `/admin/ai-config/services/${service.serviceName}`,
        body
      );
      notify('Service config updated', { type: 'success' });
      onSaved(updated);
    } catch (err) {
      logger.error('Failed to update service config', err);
      notify('Failed to update service config', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isDecisionService(service.serviceName) ? 'Structured Decision' : 'Edit Service'}: {service.serviceName.replace(/_/g, ' ')}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <Typography variant="subtitle2" color="text.secondary">Primary Model</Typography>
          <ModelSelect
            catalog={catalog}
            filter={getServiceModelFilter(service.serviceName)}
            label="Primary Model"
            value={{ provider: form.primaryModelProvider, model: form.primaryModel }}
            onChange={({ provider, model }) => setForm((prev) => ({ ...prev, primaryModelProvider: provider, primaryModel: model }))}
          />

          <Typography variant="subtitle2" color="text.secondary">Fallback Model</Typography>
          <ModelSelect
            catalog={catalog}
            filter={getServiceModelFilter(service.serviceName)}
            label="Fallback Model"
            value={{ provider: form.fallbackModelProvider, model: form.fallbackModel }}
            onChange={({ provider, model }) => setForm((prev) => ({ ...prev, fallbackModelProvider: provider, fallbackModel: model }))}
            allowNone
          />

          {!isDecisionService(service.serviceName) && <Box display="flex" gap={2}>
            <TextField
              label="Max Tokens"
              size="small"
              type="number"
              fullWidth
              value={form.maxTokens}
              onChange={(e) => setForm((prev) => ({ ...prev, maxTokens: Number(e.target.value) }))}
              inputProps={{ min: 256, max: 32768 }}
            />
            <TextField
              label="Temperature"
              size="small"
              type="number"
              fullWidth
              value={form.temperature}
              onChange={(e) => setForm((prev) => ({ ...prev, temperature: Number(e.target.value) }))}
              inputProps={{ min: 0, max: 2, step: 0.1 }}
            />
          </Box>}

          {service.serviceName === 'chat_participation' && <>
            <Typography variant="body2">Decision models use the threshold below. Chat models use their JSON decision. Failed routing stays silent; explicit mentions remain available.</Typography>
            <TextField size="small" type="number" label="Participation probability threshold" value={form.decisionSettings.threshold}
              inputProps={{ min: 0.5, max: 1, step: 0.01 }} onChange={e => setForm(prev => ({ ...prev, decisionSettings: { ...prev.decisionSettings, threshold: Number(e.target.value) } }))} />
            <TextField size="small" type="number" label="Decision timeout (ms)" value={form.decisionSettings.timeoutMs}
              inputProps={{ min: 250, max: 10000, step: 250 }} onChange={e => setForm(prev => ({ ...prev, decisionSettings: { ...prev.decisionSettings, timeoutMs: Number(e.target.value) } }))} />
          </>}
          {isDecisionService(service.serviceName) && <>
            <Typography variant="body2" color="text.secondary">{service.serviceName === 'chat_scope' ? 'Disabled skips the scope model. Observation records decisions while Elisa uses the scope instructions. Enforced returns a fixed refusal for excluded-only requests. Safety checks remain independent. Changes apply to the next operation.' : 'Disabled makes no decision calls. Observe records evaluations without changing results. Apply uses decisions to improve results. Settings take effect on the next operation.'}</Typography>
            <TextField id="decision-service-mode" select label="Mode" size="small" value={form.decisionSettings.mode}
              onChange={e => setForm(prev => ({ ...prev, decisionSettings: { ...prev.decisionSettings, mode: e.target.value as 'observe' | 'apply' } }))}>
              <MenuItem value="observe">Observe — evaluate only</MenuItem>
              <MenuItem value="apply">{service.serviceName === 'chat_scope' ? 'Enforced — refuse excluded requests' : 'Apply — improve results'}</MenuItem>
            </TextField>
            {([
              ['threshold', 'Decision probability threshold', 0.5, 1, 0.01],
              ['timeoutMs', 'Request timeout (ms)', 250, 10000, 250],
              ...(service.serviceName === 'chat_scope' ? [] : [['candidateLimit', 'Maximum candidates per evaluation', 1, 20, 1]]),
              ...(service.serviceName === 'suggestion_quality' ? [['repairLimit', 'Maximum repair rounds', 0, 1, 1]] : []),
            ] as [keyof Omit<DecisionSettings, 'mode'>, string, number, number, number][]).map(([key, label, min, max, step]) => (
              <TextField key={key} size="small" type="number" label={label} value={form.decisionSettings[key]}
                inputProps={{ min, max, step }} onChange={e => setForm(prev => ({ ...prev,
                  decisionSettings: { ...prev.decisionSettings, [key]: Number(e.target.value) } }))} />
            ))}
            <Typography variant="caption" color="text.secondary">High thresholds act only on strong evidence. These are pilot defaults, not validated accuracy guarantees. Provider failures use the existing flow; failed suggestion repairs preserve existing suggestions.</Typography>
          </>}
          {service.serviceName === 'content_moderation' && <Typography variant="body2">Safety moderation remains enabled. Choose its primary and fallback model here; the existing safety policy and crisis routing are preserved.</Typography>}
          <FormControlLabel
            control={<Switch disabled={service.serviceName === 'content_moderation'} checked={form.isEnabled} onChange={(e) => setForm((prev) => ({ ...prev, isEnabled: e.target.checked }))} />}
            label="Enabled"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
